import type { CSSProperties } from "react";
import { Star, Heart, ThumbsUp } from "lucide-react";
import type { RatingElementProps } from "./types";
import { resolveRatingStarColor, resolveDynamicColor, normalizePadding, mapTextStyle } from "./utils";

export interface RatingElementComponentProps extends RatingElementProps {
  /** Additional CSS class name */
  className?: string;
  /** Container style overrides */
  containerStyle?: CSSProperties;
}

/**
 * RatingElement - Renders a rating with stars/icons and optional value display.
 * CRITICAL: Uses resolveRatingStarColor to ensure rating stars ALWAYS use the ratingStar brand color,
 * not the accent color. This fixes the color inconsistency bugs.
 */
export function RatingElement({
  value,
  max,
  brandColors,
  templateDefaults,
  ratingStyle,
  style,
  opacity,
  borderRadius,
  borderWidth,
  borderColor,
  shadow,
  backgroundBlendMode,
  className,
  containerStyle,
}: RatingElementComponentProps) {
  // Extract rating configuration with defaults
  const showStars = ratingStyle?.showStars ?? true;
  const showValue = ratingStyle?.showValue ?? false;
  const starSize = ratingStyle?.starSize ?? 24;
  const spacing = ratingStyle?.spacing ?? 12;
  const icon = ratingStyle?.icon ?? "star";
  const customIconUrl = ratingStyle?.customIconUrl;
  
  // CRITICAL: Use resolveRatingStarColor for star color to ensure it uses ratingStar, not accent
  const starColor = resolveRatingStarColor(
    ratingStyle?.starColor,
    templateDefaults,
    brandColors
  );
  
  // Value color uses dynamic resolution (can be primaryText, accent, etc.)
  const valueColor = ratingStyle?.valueColor
    ? resolveDynamicColor(ratingStyle.valueColor, templateDefaults, brandColors, style?.color ?? brandColors.primaryText)
    : style?.color
      ? resolveDynamicColor(style.color, templateDefaults, brandColors, brandColors.primaryText)
      : brandColors.primaryText;
  
  // Resolve container styling
  const resolvedBorderColor = borderColor
    ? resolveDynamicColor(borderColor, templateDefaults, brandColors, borderColor)
    : style?.borderColor
      ? resolveDynamicColor(style.borderColor, templateDefaults, brandColors, style.borderColor)
      : undefined;
  
  const resolvedBackgroundColor = style?.backgroundColor
    ? resolveDynamicColor(style.backgroundColor, templateDefaults, brandColors, style.backgroundColor)
    : undefined;
  
  const padding = normalizePadding(style);
  
  const iconCount = Math.max(1, Math.round(max));
  const filledCount = Math.max(0, Math.min(iconCount, Math.round(value)));
  
  const containerCss: CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: showStars ? "flex-start" : "center",
    gap: showStars && showValue ? `${spacing}px` : 0,
    backgroundColor: resolvedBackgroundColor,
    borderRadius: borderRadius ?? style?.borderRadius ? `${borderRadius ?? style?.borderRadius}px` : undefined,
    borderWidth: borderWidth ?? style?.borderWidth ? `${borderWidth ?? style?.borderWidth}px` : undefined,
    borderColor: resolvedBorderColor,
    borderStyle: (borderWidth ?? style?.borderWidth) ? "solid" : undefined,
    boxShadow: shadow ?? style?.shadow ?? undefined,
    padding,
    opacity: opacity ?? style?.opacity ?? 1,
    mixBlendMode: (backgroundBlendMode ?? style?.backgroundBlendMode) as CSSProperties["mixBlendMode"] | undefined,
    ...containerStyle,
  };
  
  // Calculate icon gap (spacing between individual stars)
  const iconGap = Math.max(2, Math.round(starSize * 0.2));
  
  // Render star/icon elements
  const iconElements = showStars
    ? Array.from({ length: iconCount }).map((_, index) => {
        const isFilled = index < filledCount;
        
        // Custom icon from URL
        if (icon === "custom" && customIconUrl) {
          return (
            <img
              key={index}
              src={customIconUrl}
              alt="Rating icon"
              style={{
                width: `${starSize}px`,
                height: `${starSize}px`,
                objectFit: "contain",
                filter: isFilled ? undefined : "grayscale(0.75) opacity(0.4)",
              }}
            />
          );
        }
        
        // Lucide icon component
        const IconComponent =
          icon === "heart" ? Heart : icon === "thumb" ? ThumbsUp : Star;
        
        return (
          <IconComponent
            key={index}
            strokeWidth={isFilled ? 0 : 1.5}
            fill={isFilled ? starColor : "none"}
            style={{
              width: `${starSize}px`,
              height: `${starSize}px`,
              color: starColor,
            }}
          />
        );
      })
    : null;
  
  // Value text style
  const valueStyle: CSSProperties = {
    ...mapTextStyle(style, brandColors, templateDefaults, {
      color: valueColor,
      fontWeight: style?.fontWeight ?? 600,
    }),
    padding: undefined,
    backgroundColor: "transparent",
    borderColor: undefined,
    borderWidth: undefined,
    borderRadius: undefined,
    boxShadow: undefined,
  };
  
  if (!showStars && !showValue) {
    return null;
  }
  
  return (
    <div className={className} style={containerCss}>
      {showStars && (
        <div style={{ display: "flex", alignItems: "center", gap: `${iconGap}px` }}>
          {iconElements}
        </div>
      )}
      {showValue && (
        <span style={valueStyle}>
          {value.toFixed(1)} / {max}
        </span>
      )}
    </div>
  );
}
